import { useState, useEffect, useRef, useCallback } from 'react';

//  CSS
import formStyles from "./../../assets/css/Input.module.css";
import buttonStyles from "./../../assets/css/Button.module.css";

const OTP_LENGTH = 6;
const RESEND_COOLDOWN = 60;

// เวลาปัจจุบันเป็นวินาที (เทียบกับ expire_at ที่ API ส่งมาเป็น Unix seconds)
const nowInSeconds = () => Math.floor(Date.now() / 1000);

export default function OtpVerifyApp(props) {
    const {
        phone,
        refCode,
        expireAt,
        lastSentAt,
        resendCount = 0,
        maxResend = 3,
        wrongCount = 0,
        maxWrong = 3,
        clearSignal,
        onSubmitOtp,
        onResend,
        onExhausted
    } = props;

    const [otp, setOtp] = useState(() => new Array(OTP_LENGTH).fill(""));
    const [tick, setTick] = useState(() => nowInSeconds());
    const inputRefs = useRef([]);

    // เดินนาฬิกาทุก 1 วินาที ใช้คำนวณทั้งเวลาหมดอายุและ cooldown ปุ่มส่งซ้ำ
    useEffect(() => {
        const timer = setInterval(() => setTick(nowInSeconds()), 1000);
        return () => clearInterval(timer);
    }, []);

    // ล้างช่องกรอกเมื่อ parent สั่ง (กรอกรหัสผิด / ขอรหัสใหม่)
    useEffect(() => {
        setOtp(new Array(OTP_LENGTH).fill(""));
        setTick(nowInSeconds());
        inputRefs.current[0]?.focus();
    }, [clearSignal]);

    const secondsLeft = Math.max(0, (expireAt || 0) - tick);
    // cooldown 60 วินาที นับจาก SMS ใบล่าสุด (ไม่ผูกกับอายุรหัส — หมดอายุแล้วยังกดส่งซ้ำได้)
    const cooldownLeft = Math.max(0, RESEND_COOLDOWN - (tick - (lastSentAt || 0)));

    const otpExpired = secondsLeft === 0;
    const otpValue = otp.join("");
    const otpCompleted = otpValue.length === OTP_LENGTH;
    const resendLimitReached = resendCount >= maxResend;
    const canResend = cooldownLeft === 0 && !resendLimitReached;

    // รหัสหมดอายุ + ส่งซ้ำครบแล้ว = ทำอะไรต่อไม่ได้ ให้ parent พากลับไปเริ่มขอรหัสใหม่ (ยิงครั้งเดียว)
    const exhaustedFiredRef = useRef(false);
    useEffect(() => {
        if (otpExpired && resendLimitReached && !exhaustedFiredRef.current && onExhausted) {
            exhaustedFiredRef.current = true;
            onExhausted();
        }
    }, [otpExpired, resendLimitReached, onExhausted]);

    const formatTime = (totalSeconds) => {
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        return `${minutes}:${String(seconds).padStart(2, "0")}`;
    };

    const focusInput = (index) => {
        inputRefs.current[index]?.focus();
        inputRefs.current[index]?.select();
    };

    const setDigits = useCallback((digits, startIndex) => {
        setOtp((prev) => {
            const next = [...prev];
            for (let i = 0; i < digits.length && startIndex + i < OTP_LENGTH; i++) {
                next[startIndex + i] = digits[i];
            }
            return next;
        });
    }, []);

    const handleChange = (event, index) => {
        const raw = event.target.value;
        const digits = raw.replace(/\D/g, "");

        // ลบจริง ๆ (ค่าว่าง) เท่านั้นถึงล้างช่อง — พิมพ์อักขระที่ไม่ใช่ตัวเลขให้เมินไป ไม่ลบของเดิม
        if (raw === "") {
            setDigits([""], index);
            return;
        }

        if (digits === "") {
            return;
        }

        setDigits(digits.split(""), index);

        const nextIndex = Math.min(index + digits.length, OTP_LENGTH - 1);
        focusInput(nextIndex);
    };

    const handleKeyDown = (event, index) => {
        if (event.key === "Backspace") {
            event.preventDefault();

            if (otp[index] !== "") {
                setDigits([""], index);
                return;
            }

            if (index > 0) {
                setDigits([""], index - 1);
                focusInput(index - 1);
            }
            return;
        }

        if (event.key === "ArrowLeft" && index > 0) {
            event.preventDefault();
            focusInput(index - 1);
            return;
        }

        if (event.key === "ArrowRight" && index < OTP_LENGTH - 1) {
            event.preventDefault();
            focusInput(index + 1);
        }
    };

    const handlePaste = (event, index) => {
        event.preventDefault();
        const digits = (event.clipboardData.getData("text") || "").replace(/\D/g, "");
        if (digits === "") return;

        setDigits(digits.split(""), index);
        focusInput(Math.min(index + digits.length, OTP_LENGTH - 1));
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        if (!otpCompleted || otpExpired) return;

        onSubmitOtp(otpValue);
    };

    const renderTimer = () => {
        if (otpExpired) {
            return (
                <div className={[formStyles.otpTimerText, formStyles.otpTextError, "center"].join(" ")}>
                    รหัสหมดอายุ กรุณาขอรหัสใหม่
                </div>
            );
        }

        return (
            <div className={[formStyles.otpTimerText, "center"].join(" ")}>
                รหัสหมดอายุใน <span className={formStyles.otpTimerCount}>{formatTime(secondsLeft)}</span> นาที
            </div>
        );
    };

    // API ไม่ได้ส่งจำนวนครั้งที่เหลือกลับมา — นับจากจำนวนครั้งที่เจอ code 92 ฝั่งนี้เอง
    const renderWrongCount = () => {
        if (wrongCount <= 0 || wrongCount >= maxWrong) return null;

        return (
            <div className={[formStyles.otpTimerText, formStyles.otpTextError, "center"].join(" ")}>
                รหัสไม่ถูกต้อง กรอกผิดได้อีก {maxWrong - wrongCount} ครั้ง
            </div>
        );
    };

    const renderResendText = () => {
        if (resendLimitReached) {
            return `ส่งรหัสซ้ำครบแล้ว`;
        }

        if (cooldownLeft > 0) {
            return `ส่งรหัสอีกครั้งใน ${formatTime(cooldownLeft)}`;
        }

        return "ส่งรหัส OTP อีกครั้ง";
    };

    return (
        <>
            <br />

            <div className={[formStyles.otpHeadTitle, "center"].join(" ")}>
                ยืนยันรหัส OTP {!resendLimitReached && `(${maxResend - resendCount})`}
            </div>

            <div className={[formStyles.otpNote, "center"].join(" ")}>
                ส่งรหัส OTP ไปที่เบอร์ <span className={formStyles.otpPhoneText}>{phone}</span>
            </div>

            {
                refCode && (
                    <div className={[formStyles.otpRefText, "center"].join(" ")}>
                        รหัสอ้างอิง (Ref) : <span className={formStyles.otpRefCode}>{refCode}</span>
                    </div>
                )
            }

            <form onSubmit={handleSubmit}>
                <div className={formStyles.otpInputGroup}>
                    {
                        otp.map((digit, index) => (
                            <input
                                key={index}
                                ref={(element) => { inputRefs.current[index] = element; }}
                                id={`register_otp_${index}`}
                                type="tel"
                                inputMode="numeric"
                                autoComplete={index === 0 ? "one-time-code" : "off"}
                                className={[formStyles.inputStyles, formStyles.otpInputBox, "kanit"].join(" ")}
                                maxLength={OTP_LENGTH}
                                value={digit}
                                disabled={otpExpired}
                                onChange={(event) => handleChange(event, index)}
                                onKeyDown={(event) => handleKeyDown(event, index)}
                                onPaste={(event) => handlePaste(event, index)}
                                onFocus={(event) => event.target.select()}
                            />
                        ))
                    }
                </div>

                {renderWrongCount()}
                {renderTimer()}

                <div className={[formStyles.inputPaddingTop, "center"].join(" ")} style={{ marginTop: "20px" }}>
                    <button
                        id="register_otp_button"
                        type="submit"
                        className={[buttonStyles.btnRedirect, buttonStyles.btnRedirectComponent, formStyles.otpButton].join(" ")}
                        style={{ display: "inline-block" }}
                        disabled={!otpCompleted || otpExpired}
                    >
                        <div style={{ padding: "4px 0px" }}>
                            ยืนยันรหัส OTP
                        </div>
                    </button>
                </div>
            </form>

            <div className={[formStyles.otpRefText, "center"].join(" ")}>
                ไม่ได้รับรหัส ? <span
                    id="register_otp_resend"
                    className={[formStyles.otpInputResend, canResend ? "" : formStyles.otpInputResendDisabled].join(" ")}
                    onClick={() => canResend && onResend()}
                >
                    {renderResendText()}
                </span>
            </div>
        </>
    );
}
